import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { formatGuideLongDate } from './guidesUtils';
import GuideItemIcon, { guideItemKind } from './GuideItemIcon';

function plural(count, singular, pluralLabel) {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? singular : pluralLabel}`;
}

function statusText(item) {
  if (item.isCollection && item.readOnly) return 'Collection';
  if (item.isCollection && item.canEditCopy) return 'Collection copy';
  if (item.published) return 'Published';
  if (item.isShared) return 'Shared';
  return 'Draft';
}

function countParts(item) {
  const kind = guideItemKind(item);
  const parts = [];
  if (kind === 'notebook') {
    if (item.sectionCount != null) parts.push(plural(item.sectionCount, 'section', 'sections'));
    if (item.topicCount != null) parts.push(plural(item.topicCount, 'topic', 'topics'));
  } else if (kind === 'deck') {
    if (item.sectionCount != null) parts.push(plural(item.sectionCount, 'section', 'sections'));
    if (item.setCount != null) parts.push(plural(item.setCount, 'set', 'sets'));
  } else if (kind === 'set' && item.cardCount != null) {
    parts.push(plural(item.cardCount, 'card', 'cards'));
  }
  return parts;
}

function ItemMenu({ item, kind, onOpen, showRemove, onRemoveCollection, showDelete, onDelete }) {
  return (
    <Dropdown align="end" className="guides-item-menu" onClick={(e) => e.stopPropagation()}>
      <Dropdown.Toggle
        variant="light-lite"
        size="sm"
        className="guides-item-menu-toggle no-arrow"
        id={`guide-item-menu-${kind}-${item.id}-${item.collectionId || 'own'}`}
      >
        <i className="bi bi-three-dots" aria-hidden />
        <span className="visually-hidden">Item options</span>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={onOpen}>
          <i className="bi bi-box-arrow-up-right me-2" aria-hidden />
          Open
        </Dropdown.Item>
        {showRemove ? (
          <Dropdown.Item onClick={onRemoveCollection}>
            <i className="bi bi-bookmark-dash me-2" aria-hidden />
            Remove from collection
          </Dropdown.Item>
        ) : null}
        {showDelete ? (
          <Dropdown.Item className="text-danger" onClick={onDelete}>
            <i className="bi bi-trash me-2" aria-hidden />
            Delete
          </Dropdown.Item>
        ) : null}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default function GuideListItem({
  item,
  view = 'list',
  onOpen,
  onDelete,
  onRemoveCollection
}) {
  const kind = guideItemKind(item);
  const counts = countParts(item);
  const date = formatGuideLongDate(item.updatedAt || item.createdAt);
  const listMeta = [
    item.category,
    ...counts,
    date
  ].filter(Boolean);
  const showDelete = typeof onDelete === 'function' && item.isOwner && !item.readOnly;
  const showRemove = typeof onRemoveCollection === 'function' && item.isCollection && item.collectionId;
  const isGrid = view === 'grid';
  const menu = (
    <ItemMenu
      item={item}
      kind={kind}
      onOpen={onOpen}
      showRemove={showRemove}
      onRemoveCollection={onRemoveCollection}
      showDelete={showDelete}
      onDelete={onDelete}
    />
  );

  return (
    <div className={`guides-item ${isGrid ? 'guides-item-card' : ''}`}>
      <button type="button" className="guides-item-main" onClick={onOpen}>
        {isGrid ? (
          <>
            <span className="guides-item-head">
              <span className={`guides-item-icon guides-item-icon-${kind}`}>
                <GuideItemIcon item={item} />
              </span>
              <span className="guides-item-body">
                <span className="guides-item-title">{item.title}</span>
                {(counts.length > 0 || date) ? (
                  <span className="guides-item-meta">
                    {counts.map((part) => (
                      <span key={part}>{part}</span>
                    ))}
                    {date ? <span>{date}</span> : null}
                  </span>
                ) : null}
              </span>
            </span>
            {item.description ? (
              <span className="guides-item-desc">{item.description}</span>
            ) : null}
          </>
        ) : (
          <>
            <span className={`guides-item-icon guides-item-icon-${kind}`}>
              <GuideItemIcon item={item} />
            </span>
            <span className="guides-item-body">
              <span className="guides-item-title">{item.title}</span>
              {item.description ? (
                <span className="guides-item-desc">{item.description}</span>
              ) : null}
              {listMeta.length > 0 ? (
                <span className="guides-item-meta">
                  {listMeta.map((part, idx) => (
                    <span key={`${part}-${idx}`}>{part}</span>
                  ))}
                </span>
              ) : null}
            </span>
          </>
        )}
      </button>
      <div className="guides-item-aside">
        <span className="guides-item-status">{statusText(item)}</span>
        {isGrid && item.category ? (
          <span className="guides-item-status">{item.category}</span>
        ) : null}
        {menu}
      </div>
    </div>
  );
}
